import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { boolean, object } from "yup";
import SxForm from "../../forms/SxForm";
import SxCheckbox from "../../forms/fields/SxCheckbox";
import SubmitButton from "../../components/SubmitButton";
import { authService } from "../../services/auth/authService";
import { premiumSponsorshipService } from "../../services/premiumSponsorship/premiumSponsorshipService";
import { PremiumSponsorshipPreview } from "../../services/premiumSponsorship/premiumSponsorshipModel";
import { loggerBuilder } from "../../services/logger";

const { declinePremiumSponsorship, acceptPremiumSponsorship } =
  premiumSponsorshipService();
const { isLoggedIn } = authService();
const logger = loggerBuilder("premium-sponsorship-consent");

interface Props {
  token: string;
  sponsorship: PremiumSponsorshipPreview;
}

const PremiumSponsorshipConsentScreen = ({ token, sponsorship }: Props) => {
  const { t } = useTranslation(["accounts"]);
  const navigate = useNavigate();
  const [declined, setDeclined] = useState(false);
  const [declining, setDeclining] = useState(false);
  const [emailMismatch, setEmailMismatch] = useState(false);

  if (sponsorship.status !== "invited") {
    return (
      <div className="info --compact">
        {t("accounts:premium-sponsorship.consent.ALREADY_RESPONDED")}
      </div>
    );
  }

  if (declined) {
    return (
      <div className="info --compact">
        {t("accounts:premium-sponsorship.consent.DECLINED")}
      </div>
    );
  }

  return (
    <div>
      <h1 className="page_title">
        {t("accounts:premium-sponsorship.consent.TITLE", {
          replace: { organization: sponsorship.organizationName },
        })}
      </h1>
      <p className="lblock">
        {t("accounts:premium-sponsorship.consent.SUBTITLE")}
      </p>

      {emailMismatch && (
        <div className="info --warning lblock">
          {t("accounts:premium-sponsorship.consent.EMAIL_MISMATCH")}
        </div>
      )}

      <SxForm
        initialValues={{ accepted: false }}
        validationSchema={object({
          accepted: boolean()
            .oneOf(
              [true],
              t("accounts:premium-sponsorship.consent.errors.MUST_ACCEPT"),
            )
            .required(),
        })}
        onSubmit={async () => {
          // Consent (the checkbox + this click) is already given — the detour
          // through login/register only exists to establish who's consenting.
          // Acceptance itself is finalized server-side as part of that
          // login/register call, keyed off `sponsorshipToken`.
          // `email` is carried along on both branches: it prefills the form,
          // and lets login tell the candidate why nothing happened when they
          // sign in with another account of theirs — acceptance is only
          // granted server-side on the invited address.
          const redirectToAuth = () =>
            navigate(
              `${
                sponsorship.hasAccount ? "/login" : "/register"
              }?email=${encodeURIComponent(
                sponsorship.email,
              )}&sponsorshipToken=${encodeURIComponent(token)}`,
            );

          // Asked live rather than captured at render: the session can have
          // died in between (expired JWT, 401 on an earlier call), and the
          // interceptor in `api.ts` drops the stored user and the
          // Authorization header without anything re-rendering this screen.
          if (!isLoggedIn()) {
            redirectToAuth();
            return;
          }

          try {
            await acceptPremiumSponsorship(token);
            window.location.href = "/account";
          } catch (err: any) {
            if (err?.response?.status === 403) {
              setEmailMismatch(true);
            } else if (err?.response?.status === 401) {
              // Session gone under our feet (typically an expired token).
              // Nothing to report: signing in is exactly what's needed, and
              // it accepts the sponsorship on the way.
              redirectToAuth();
            } else {
              logger.error(err);
            }
          }
        }}
      >
        <hr className="separator" />

        <SxCheckbox
          name="accepted"
          label={
            <span
              dangerouslySetInnerHTML={{
                __html: t("accounts:premium-sponsorship.consent.ACCEPT_LABEL"),
              }}
            />
          }
        />
        <div className="lblock--l lrow --stretch--h">
          <button
            type="button"
            className="btn--grey"
            disabled={declining}
            onClick={() => {
              setDeclining(true);
              declinePremiumSponsorship(token)
                .then(() => setDeclined(true))
                .catch((err) => logger.error(err))
                .finally(() => setDeclining(false));
            }}
          >
            {t("accounts:premium-sponsorship.consent.DECLINE_BUTTON")}
          </button>
          <SubmitButton type="submit" className="btn--1">
            {t("accounts:premium-sponsorship.consent.CONFIRM_BUTTON")}
          </SubmitButton>
        </div>
      </SxForm>
    </div>
  );
};

export default PremiumSponsorshipConsentScreen;
